import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import FloatingButtons from '../../../components/feature/FloatingButtons';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import InternalLinks from '../../../components/feature/InternalLinks';

export default function OSBGecisKontrolSistemi() {
  useEffect(() => {
    document.title = 'OSB Geçiş Kontrol Sistemi | Fiber Ek';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Organize Sanayi Bölgeleri için profesyonel geçiş kontrol sistemleri. Bariyer, turnike, kart okuyucu ve plaka tanıma sistemleri ile tam güvenlik.');
    }

    // Canonical link
    const canonical = document.createElement('link');
    canonical.rel = 'canonical';
    canonical.href = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/osb-gecis-kontrol-sistemi`;
    document.head.appendChild(canonical);

    // Open Graph tags
    const ogTitle = document.createElement('meta');
    ogTitle.setAttribute('property', 'og:title');
    ogTitle.content = 'OSB Geçiş Kontrol Sistemi | Fiber Ek';
    document.head.appendChild(ogTitle);

    const ogDescription = document.createElement('meta');
    ogDescription.setAttribute('property', 'og:description');
    ogDescription.content = 'Organize Sanayi Bölgeleri için profesyonel geçiş kontrol sistemleri. Bariyer, turnike, kart okuyucu ve plaka tanıma sistemleri ile tam güvenlik.';
    document.head.appendChild(ogDescription);

    const ogUrl = document.createElement('meta');
    ogUrl.setAttribute('property', 'og:url');
    ogUrl.content = `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/osb-gecis-kontrol-sistemi`;
    document.head.appendChild(ogUrl);

    const ogType = document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    ogType.content = 'website';
    document.head.appendChild(ogType);

    const ogLocale = document.createElement('meta');
    ogLocale.setAttribute('property', 'og:locale');
    ogLocale.content = 'tr_TR';
    document.head.appendChild(ogLocale);

    // Service JSON-LD
    const schema = document.createElement('script');
    schema.type = 'application/ld+json';
    schema.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "OSB Geçiş Kontrol Sistemi",
      "description": "Organize Sanayi Bölgeleri için profesyonel geçiş kontrol sistemleri. Bariyer, turnike, kart okuyucu ve plaka tanıma sistemleri ile tam güvenlik.",
      "provider": {
        "@type": "LocalBusiness",
        "name": "Fiber Ek",
        "telephone": "+905537165561",
        "url": import.meta.env.VITE_SITE_URL
      },
      "areaServed": {
        "@type": "Place",
        "name": "İstanbul, Kocaeli, Sakarya, Bursa, Tekirdağ, Yalova"
      },
      "url": `${import.meta.env.VITE_SITE_URL}/hizmetlerimiz/osb-gecis-kontrol-sistemi`
    });
    document.head.appendChild(schema);

    return () => {
      document.head.removeChild(canonical);
      document.head.removeChild(ogTitle);
      document.head.removeChild(ogDescription);
      document.head.removeChild(ogUrl);
      document.head.removeChild(ogType);
      document.head.removeChild(ogLocale);
      document.head.removeChild(schema);
    };
  }, []);

  const breadcrumbItems = [
    { label: 'Anasayfa', href: '/' },
    { label: 'Hizmetlerimiz', href: '/#hizmetlerimiz' },
    { label: 'OSB Geçiş Kontrol Sistemi' }
  ];

  const faqData = [
    {
      question: "Geçiş kontrol sistemi neden gereklidir?",
      answer: "OSB'lerde güvenlik ve personel takibi için zorunludur. Yetkisiz girişleri engeller ve çalışma saatlerini otomatik kaydeder."
    },
    {
      question: "Turnike sistemleri nasıl çalışır?",
      answer: "Kart, parmak izi veya yüz tanıma ile kimlik doğrulama yaparak geçişe izin verir. Tek yönlü veya çift yönlü geçiş sağlar."
    },
    {
      question: "Bariyer sistemleri hangi araçlar için uygundur?",
      answer: "Otomobil, kamyon ve servis araçları için uygundur. Plaka tanıma sistemi ile otomatik açılım sağlar."
    },
    {
      question: "Parmak izi tanıma güvenli midir?",
      answer: "Evet, biyometrik veriler şifrelenmiş olarak saklanır ve çoğaltılamaz. En güvenli kimlik doğrulama yöntemlerinden biridir."
    },
    {
      question: "Sistem kesintisinde ne olur?",
      answer: "UPS (kesintisiz güç kaynağı) ile sistem çalışmaya devam eder. Acil durumlarda manuel açma özelliği mevcuttur."
    }
  ];

  return (
    <>
      <div className="min-h-screen bg-white">
        <Navbar />
        
        {/* Hero Section */}
        <section className="relative pt-24 pb-16 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="absolute inset-0">
            <img 
              src="https://public.readdy.ai/ai/img_res/4e44ea5b4101172486337406b64d5806.jpg"
              alt="Kocaeli OSB Sanayi Bölgesi"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-slate-900/80 via-slate-800/70 to-slate-900/80"></div>
          </div>
          
          <div className="container mx-auto px-4 relative z-10">
            <Breadcrumb items={breadcrumbItems} />
            <div className="max-w-4xl mx-auto text-center text-white pt-8">
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                OSB Geçiş Kontrol Sistemi
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                Kocaeli OSB Sanayi Tesisleri İçin Profesyonel Geçiş Kontrol ve Güvenlik Sistemleri
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="tel:+905537165561" 
                  className="px-8 py-4 bg-green-500 text-white font-bold rounded-full hover:bg-green-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-phone-line mr-2"></i>
                  +90 553 716 55 61
                </a>
                <a 
                  href="https://wa.me/905537165561" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-8 py-4 bg-cyan-500 text-white font-bold rounded-full hover:bg-cyan-600 transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line mr-2"></i>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Proje Detayları */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Kocaeli OSB Geçiş Kontrol Projesi
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Kocaeli Organize Sanayi Bölgesi'ndeki fabrikalar için özel olarak tasarlanmış geçiş kontrol sistemi kurulum projemiz. Modern sanayi tesislerinin güvenlik ve personel takibi ihtiyaçlarını karşılıyoruz.
                </p>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-500 mr-3 text-xl"></i>
                    <span><strong>Geçiş kontrol sistemleri</strong> ile güvenli erişim</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-500 mr-3 text-xl"></i>
                    <span><strong>Turnike sistemleri</strong> ile personel kontrolü</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-500 mr-3 text-xl"></i>
                    <span><strong>Bariyer sistemleri</strong> ile araç kontrolü</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-500 mr-3 text-xl"></i>
                    <span><strong>Kart okuyucu ve biyometrik</strong> tanıma</span>
                  </li>
                  <li className="flex items-center">
                    <i className="ri-check-line text-green-500 mr-3 text-xl"></i>
                    <span><strong>7/24 sistem izleme</strong> ve bakım</span>
                  </li>
                </ul>
              </div>
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=access%20control%20turnstile%20installation%20at%20Kocaeli%20industrial%20facility%20entrance%2C%20security%20gates%20with%20card%20readers%2C%20professional%20access%20control%20equipment%2C%20factory%20entrance%20security&width=800&height=600&seq=kocaeli-access-control&orientation=landscape"
                  alt="Kocaeli OSB Geçiş Kontrol Sistemi"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Geçiş Kontrol Sistemleri */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Geçiş Kontrol Sistemlerimiz
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-door-open-line text-2xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Turnike Sistemleri</h3>
                <p className="text-gray-600">
                  Üç kollu, tam boy ve hızlı geçiş turnikeleri. Kocaeli OSB fabrikaları için dayanıklı ve güvenli çözümler.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-roadster-line text-2xl text-blue-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Bariyer Sistemleri</h3>
                <p className="text-gray-600">
                  Otomatik araç bariyerleri ve plaka tanıma sistemi. Sanayi tesisleri için güvenli araç giriş-çıkış kontrolü.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-bank-card-line text-2xl text-purple-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Kart Okuyucu Sistemleri</h3>
                <p className="text-gray-600">
                  RFID kart, proximity kart ve akıllı kart okuyucuları. Personel kartları ile hızlı ve güvenli geçiş.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-fingerprint-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Parmak İzi Tanıma</h3>
                <p className="text-gray-600">
                  Biyometrik parmak izi tanıma sistemi. Yüksek güvenlik gerektiren alanlar için ideal çözüm.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-user-search-line text-2xl text-red-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Yüz Tanıma Sistemi</h3>
                <p className="text-gray-600">
                  AI destekli yüz tanıma teknolojisi. Temassız ve hızlı kimlik doğrulama imkanı sunar.
                </p>
              </div>
              <div className="bg-white p-8 rounded-2xl shadow-lg">
                <div className="w-16 h-16 bg-cyan-100 rounded-full flex items-center justify-center mb-6">
                  <i className="ri-time-line text-2xl text-cyan-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Zaman Takip Sistemi</h3>
                <p className="text-gray-600">
                  Personel giriş-çıkış saatlerini otomatik kayıt altına alan zaman ve devam takip sistemi.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Kocaeli OSB Hakkında */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src="https://readdy.ai/api/search-image?query=Kocaeli%20Organize%20Sanayi%20Bolgesi%20industrial%20facilities%20with%20modern%20factories%2C%20organized%20manufacturing%20district%2C%20large%20industrial%20complex%20aerial%20view&width=800&height=600&seq=kocaeli-osb-aerial&orientation=landscape"
                  alt="Kocaeli OSB Görünümü"
                  className="w-full h-auto rounded-2xl shadow-xl"
                />
              </div>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  Kocaeli OSB Güvenlik Altyapısı
                </h2>
                <p className="text-gray-600 text-lg mb-6">
                  Kocaeli Organize Sanayi Bölgesi, Türkiye'nin en büyük sanayi merkezlerinden biridir. Bu bölgedeki fabrikaların geçiş kontrol ve güvenlik sistemleri ihtiyaçlarını profesyonelce karşılıyoruz.
                </p>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <i className="ri-factory-line text-green-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Büyük Sanayi Tesisleri</h4>
                      <p className="text-gray-600">Binlerce personelin çalıştığı mega fabrikalar</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-shield-check-line text-green-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Yüksek Güvenlik</h4>
                      <p className="text-gray-600">Sanayi güvenlik standartlarına uygun sistemler</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <i className="ri-user-settings-line text-green-500 mr-3 text-xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Personel Yönetimi</h4>
                      <p className="text-gray-600">Otomatik giriş-çıkış takibi ve raporlama</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Sistem Özellikleri */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Gelişmiş Sistem Özellikleri
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="text-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-database-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Merkezi Yönetim</h3>
                <p className="text-gray-600 text-sm">Tüm geçişleri tek merkezden kontrol</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-bar-chart-line text-3xl text-blue-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Detaylı Raporlar</h3>
                <p className="text-gray-600 text-sm">Giriş-çıkış ve devam raporları</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-smartphone-line text-3xl text-purple-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Mobil Uygulama</h3>
                <p className="text-gray-600 text-sm">Telefondan sistem yönetimi</p>
              </div>
              <div className="text-center">
                <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className="ri-alert-line text-3xl text-orange-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Anında Uyarılar</h3>
                <p className="text-gray-600 text-sm">Yetkisiz giriş denemelerinde bildirim</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-900 mb-16">
              Sıkça Sorulan Sorular
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqData.map((faq, index) => (
                <div key={index} className="bg-gray-50 rounded-2xl shadow-lg p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">{faq.question}</h4>
                  <p className="text-gray-600">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gradient-to-r from-green-600 to-teal-600">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              OSB Geçiş Kontrol Sistemi İçin İletişime Geçin
            </h2>
            <p className="text-green-100 text-xl mb-8 max-w-2xl mx-auto">
              Kocaeli OSB'deki fabrikanız için profesyonel geçiş kontrol ve güvenlik sistemleri. Hemen teklif alın.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:+905537165561" 
                className="px-8 py-4 bg-white text-green-600 font-bold rounded-full hover:bg-gray-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                +90 553 716 55 61
              </a>
              <a 
                href="https://wa.me/905537165561" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-8 py-4 bg-cyan-500 text-white font-bold rounded-full hover:bg-cyan-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line mr-2"></i>
                WhatsApp İletişim
              </a>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}